import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CalendarDays, ClipboardList, Eraser, Save, Target, Trash2, UserRound } from 'lucide-react'
import { PeriodSelector } from '@/components/analytics'
import { Badge, Button, Card, ProgressBar } from '@/components/ui'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { PedagogyBackLink } from '@/components/pedagogy/PedagogyBackLink'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { withAuthRecovery } from '@/lib/authRecovery'
import {
  createTrainerChecklistReviewLink,
  formatTrainerReviewDate,
  openTrainerChecklistReport,
  splitTrainerReviewDateTime,
} from '@/lib/trainerChecklistReviews'
import { formatNumber, formatPartnerDisplay, type MetricPeriod } from '@/lib/utils'
import { getSupabase } from '@/lib/supabase'
import { useAuthStore, useStudioStore } from '@/store'
import { PedagogyAccessErrorCard } from '@/components/pedagogy/PedagogyAccessStates'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import {
  getPedagogySpecialistPeriodDetails,
  listPedagogyDashboard,
  savePedagogyWeeklyPlan,
  type PedagogyDashboardRow,
  type PedagogySpecialistPeriodRow,
  type PedagogyWeeklyPlanMatrixRow,
} from '@/services/pedagogyDashboardService'
import { getPedagogySpecialistPartners } from '@/services/pedagogySpecialistService'
import { archiveTrainerReview, listAllTrainerReviewSummary, type TrainerReviewSummaryRow } from '@/services/trainerReviewService'
import type { PedagogySpecialistPartner } from '@/types'

type SpecialistTab = 'overview' | 'plan' | 'reviews' | 'teachers'

const TABS: Array<{ id: SpecialistTab; label: string }> = [
  { id: 'overview', label: 'Сводка' },
  { id: 'reviews', label: 'Отсмотры' },
  { id: 'teachers', label: 'Педагоги' },
]

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100] as const

type WeeklyPlanDraft = {
  week1: string
  week2: string
  week3: string
  week4: string
  week5: string
}

type PlanCompletenessFilter = 'all' | 'no_plan' | 'partial_plan' | 'full_plan'
type PlanCompletenessStatus = Exclude<PlanCompletenessFilter, 'all'> | 'not_applicable'
type DistributionMode = 'direction' | 'city' | 'partner' | 'coach' | 'plan_completion'

type DistributionItem = {
  label: string
  primaryText: string
  secondaryText: string
  barValue: number
  sortBucket: 0 | 1
  sortValue: number
}

type PlanCompletionDistributionItem = {
  label: string
  primaryText: string
  secondaryText: string
  barValue: number
  sortBucket: number
  sortValue: number
  hasFact: boolean
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatShortDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU')
}

function formatTrainingDate(value: string): string {
  const parts = splitTrainerReviewDateTime(value)
  return parts.date ? formatTrainerReviewDate(parts.date) : '—'
}

function formatTrainingTime(value: string): string {
  const parts = splitTrainerReviewDateTime(value)
  return parts.date ? `${parts.hour}:${parts.minute}` : '—'
}

function getDaysLeftInRange(startIso: string | null, endIso: string | null): number | null {
  if (!startIso || !endIso) return null

  const today = new Date()
  const currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const start = new Date(`${startIso}T00:00:00`)
  const end = new Date(`${endIso}T00:00:00`)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  if (currentDate > end) return 0

  const anchor = currentDate < start ? start : currentDate
  return Math.floor((end.getTime() - anchor.getTime()) / 86400000) + 1
}

function getSpecialistName(rows: PedagogySpecialistPeriodRow[], reviews: TrainerReviewSummaryRow[]): string {
  const reviewerName = reviews[0]?.reviewerDisplayName?.trim()
  if (reviewerName) return reviewerName
  return 'Специалист'
}

function canPlanWeek(row: PedagogyWeeklyPlanMatrixRow, weekNumber: number): boolean {
  if (weekNumber === 1) return row.canPlanWeek1
  if (weekNumber === 2) return row.canPlanWeek2
  if (weekNumber === 3) return row.canPlanWeek3
  if (weekNumber === 4) return row.canPlanWeek4
  if (weekNumber === 5) return row.canPlanWeek5
  return false
}

function getAvailableWeekNumbers(row: PedagogyWeeklyPlanMatrixRow, weekNumbers: number[]): number[] {
  return weekNumbers.filter((weekNumber) => canPlanWeek(row, weekNumber))
}

function toWeeklyPlanDraft(row: PedagogyWeeklyPlanMatrixRow): WeeklyPlanDraft {
  return {
    week1: row.canPlanWeek1 && row.planWeek1 > 0 ? String(row.planWeek1) : '',
    week2: row.canPlanWeek2 && row.planWeek2 > 0 ? String(row.planWeek2) : '',
    week3: row.canPlanWeek3 && row.planWeek3 > 0 ? String(row.planWeek3) : '',
    week4: row.canPlanWeek4 && row.planWeek4 > 0 ? String(row.planWeek4) : '',
    week5: row.canPlanWeek5 && row.planWeek5 > 0 ? String(row.planWeek5) : '',
  }
}

function normalizeWeekValue(value: string, strict = true): number {
  const trimmed = value.trim()
  if (!trimmed) return 0
  const parsed = Number.parseInt(trimmed, 10)
  if (!Number.isFinite(parsed) || parsed < 0) {
    if (!strict) return 0
    throw new Error('План должен быть неотрицательным целым числом')
  }
  return parsed
}

function sumWeeklyDraft(draft: WeeklyPlanDraft, weekNumbers: number[]): number {
  return weekNumbers.reduce((sum, weekNumber) => {
    const key = `week${weekNumber}` as keyof WeeklyPlanDraft
    return sum + normalizeWeekValue(draft[key], false)
  }, 0)
}

function getPlannedWeeksCount(draft: WeeklyPlanDraft, weekNumbers: number[]): number {
  return weekNumbers.reduce((count, weekNumber) => {
    const key = `week${weekNumber}` as keyof WeeklyPlanDraft
    return count + (normalizeWeekValue(draft[key], false) > 0 ? 1 : 0)
  }, 0)
}

function getPlanCompletenessStatus(draft: WeeklyPlanDraft, weekNumbers: number[]): PlanCompletenessStatus {
  if (weekNumbers.length === 0) return 'not_applicable'
  const plannedWeeksCount = getPlannedWeeksCount(draft, weekNumbers)
  if (plannedWeeksCount === 0) return 'no_plan'
  if (plannedWeeksCount < weekNumbers.length) return 'partial_plan'
  return 'full_plan'
}

function getPlanCompletenessBadge(status: PlanCompletenessStatus): { text: string; variant: 'success' | 'warning' | 'neutral' } {
  if (status === 'not_applicable') return { text: 'Не требуется', variant: 'neutral' }
  if (status === 'no_plan') return { text: 'Нет плана', variant: 'neutral' }
  if (status === 'partial_plan') return { text: 'План неполный', variant: 'warning' }
  return { text: 'План есть', variant: 'success' }
}

function getSavedPlanCompletenessStatus(row: PedagogyWeeklyPlanMatrixRow, weekNumbers: number[]): PlanCompletenessStatus {
  return getPlanCompletenessStatus(toWeeklyPlanDraft(row), getAvailableWeekNumbers(row, weekNumbers))
}

function getDistributionTotalValue(value: string): number {
  return normalizeWeekValue(value.trim(), false)
}

function hasOnlyPeriodTotalFilled(
  draft: WeeklyPlanDraft,
  periodTotalInput: string | undefined,
  weekNumbers: number[]
): boolean {
  return getPlannedWeeksCount(draft, weekNumbers) === 0
    && getDistributionTotalValue(periodTotalInput ?? '') > 0
}

function getPlanCompletionSlice(row: PedagogyWeeklyPlanMatrixRow, weekNumber: number | null): { plan: number; fact: number } {
  if (!weekNumber) {
    return {
      plan: row.planTotal,
      fact: row.factTotal,
    }
  }

  const planKey = `planWeek${weekNumber}` as keyof PedagogyWeeklyPlanMatrixRow
  const factKey = `factWeek${weekNumber}` as keyof PedagogyWeeklyPlanMatrixRow
  return {
    plan: Number(row[planKey] ?? 0),
    fact: Number(row[factKey] ?? 0),
  }
}

export function PedagogySpecialistDetailPage() {
  const { specialistUserId = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { studios } = useStudioStore()
  const {
    canManagePedagogy,
    canViewAllPedagogy,
    canViewPedagogySection,
    isPedagogySpecialist,
    loading: accessLoading,
    accessError,
    retryAccessCheck,
  } = usePedagogyAccess()
  const [searchParams, setSearchParams] = useSearchParams()
  const [periods, setPeriods] = useState<MetricPeriod[]>([])
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null)
  const [rows, setRows] = useState<PedagogySpecialistPeriodRow[]>([])
  const [planMatrixRows, setPlanMatrixRows] = useState<PedagogyWeeklyPlanMatrixRow[]>([])
  const [specialistDashboardRow, setSpecialistDashboardRow] = useState<PedagogyDashboardRow | null>(null)
  const [reviews, setReviews] = useState<TrainerReviewSummaryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [archivingReviewId, setArchivingReviewId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [saveMessage, setSaveMessage] = useState('')
  const [draftPlan, setDraftPlan] = useState<Record<number, WeeklyPlanDraft>>({})
  const [distributionTotals, setDistributionTotals] = useState<Record<number, string>>({})
  const [distributionMode, setDistributionMode] = useState<DistributionMode>('direction')
  const [showAllPlanCompletionRows, setShowAllPlanCompletionRows] = useState(false)
  const [partnersSheetOpen, setPartnersSheetOpen] = useState(false)
  const [reviewSearch, setReviewSearch] = useState('')
  const [reviewsPageSize, setReviewsPageSize] = useState<number>(20)
  const [reviewsPage, setReviewsPage] = useState(1)
  const [planCompletenessFilter, setPlanCompletenessFilter] = useState<PlanCompletenessFilter>('all')
  const [assignedPartners, setAssignedPartners] = useState<PedagogySpecialistPartner[]>([])

  const requestedTab = searchParams.get('tab') as SpecialistTab | null
  const activeTab = (requestedTab === 'reviews' ? 'reviews' : 'overview') as SpecialistTab
  const returnTo = `${location.pathname}${location.search}`

  const selectedPeriod = useMemo(
    () => periods.find(period => period.period_id === selectedPeriodId) ?? null,
    [periods, selectedPeriodId]
  )

  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    timeFilterOptions,
    selectedRange,
  } = useAnalyticsTimeFilter(selectedPeriod, { isCurrentPeriod: true })

  useEffect(() => {
    if (accessLoading || accessError) return
    if (!canViewPedagogySection) {
      navigate('/management-dashboard', { replace: true })
      return
    }
    if (!canViewAllPedagogy && user?.id && specialistUserId !== user.id) {
      navigate(`${pedagogyPaths.specialist(user.id)}?tab=overview`, { replace: true })
    }
  }, [accessError, accessLoading, canViewAllPedagogy, canViewPedagogySection, navigate, specialistUserId, user?.id])

  useEffect(() => {
    let cancelled = false

    async function loadPeriods() {
      const supabase = getSupabase()
      const { data, error: periodsError } = await withAuthRecovery(() =>
        supabase.from('metric_periods').select('*').order('period_start', { ascending: false })
      )
      if (periodsError) {
        throw periodsError
      }

      if (cancelled) return
      const gateDate = new Date()
      gateDate.setDate(gateDate.getDate() + 3)
      const openUntil = formatIsoDate(gateDate)
      const today = formatIsoDate(new Date())
      const nextPeriods = ((data ?? []) as MetricPeriod[]).filter(period => period.period_start <= openUntil)
      setPeriods(nextPeriods)

      const searchPeriodId = Number(searchParams.get('periodId') || '')
      if (Number.isFinite(searchPeriodId) && nextPeriods.some(period => period.period_id === searchPeriodId)) {
        setSelectedPeriodId(searchPeriodId)
        return
      }

      const current = nextPeriods.find(period => period.period_start <= today && today <= period.period_end) ?? nextPeriods[0] ?? null
      setSelectedPeriodId(current?.period_id ?? null)
    }

    void loadPeriods().catch(loadError => {
      if (cancelled) return
      console.error('Failed to load pedagogy periods:', loadError)
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить периоды')
    })

    return () => {
      cancelled = true
    }
  }, [searchParams])

  useEffect(() => {
    if (!specialistUserId || selectedPeriodId == null) return

    let cancelled = false

    async function loadData() {
      setLoading(true)
      setError('')
      setSaveMessage('')

      try {
        const [detailRows, specialistReviews, partnerRows] = await Promise.all([
          getPedagogySpecialistPeriodDetails(specialistUserId, selectedPeriodId, selectedRange?.weekNumber ?? null),
          listAllTrainerReviewSummary({ reviewerUserId: specialistUserId }),
          getPedagogySpecialistPartners(specialistUserId, selectedPeriodId),
        ])
        const dashboardRows = await listPedagogyDashboard(selectedPeriodId, selectedRange?.weekNumber ?? null)

        if (cancelled) return

        setRows(detailRows)
        setReviews(specialistReviews)
        setPlanMatrixRows([])
        setAssignedPartners(partnerRows)
        setSpecialistDashboardRow(
          dashboardRows.find((row) => row.specialistUserId === specialistUserId) ?? null
        )
        setDraftPlan({})
        setDistributionTotals({})
      } catch (loadError) {
        if (cancelled) return
        console.error('Failed to load pedagogy specialist detail:', loadError)
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить данные специалиста')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadData()
    return () => {
      cancelled = true
    }
  }, [selectedPeriodId, selectedRange?.weekNumber, specialistUserId])

  useEffect(() => {
    if (selectedPeriodId == null) return
    const next = new URLSearchParams(searchParams)
    next.set('periodId', String(selectedPeriodId))
    if (!next.get('tab')) next.set('tab', activeTab)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [activeTab, searchParams, selectedPeriodId, setSearchParams])

  useEffect(() => {
    setReviewsPage(1)
  }, [reviewSearch, reviewsPageSize, selectedPeriodId])

  const selectedPeriodIndex = useMemo(
    () => periods.findIndex(period => period.period_id === selectedPeriodId),
    [periods, selectedPeriodId]
  )

  const specialistName = useMemo(
    () => getSpecialistName(rows, reviews),
    [reviews, rows]
  )

  const summary = useMemo(() => {
    const plan = specialistDashboardRow?.plannedReviewsCount ?? 0
    const fact = specialistDashboardRow?.factReviewsCount ?? 0
    const inScopeStudios = rows.filter(row => row.inScope).length
    const closedButKept = rows.filter(row => row.closedButKept).length
    const remaining = Math.max(plan - fact, 0)
    return {
      plan,
      fact,
      remaining,
      inScopeStudios,
      closedButKept,
      completionPct: plan > 0 ? Math.round((fact / plan) * 1000) / 10 : null,
    }
  }, [rows, specialistDashboardRow])

  const planPace = useMemo(() => {
    const daysLeft = getDaysLeftInRange(selectedRange?.start ?? selectedPeriod?.period_start ?? null, selectedRange?.end ?? selectedPeriod?.period_end ?? null)
    if (summary.plan <= 0) {
      return { daysLeft, perDay: null }
    }
    if (daysLeft == null || daysLeft <= 0) {
      return { daysLeft, perDay: null }
    }
    return {
      daysLeft,
      perDay: Math.ceil(summary.remaining / daysLeft),
    }
  }, [selectedPeriod?.period_end, selectedPeriod?.period_start, selectedRange?.end, selectedRange?.start, summary.plan, summary.remaining])

  const visibleReviews = useMemo(() => {
    if (!selectedRange) return reviews
    return reviews
      .filter(review => review.reviewDate >= selectedRange.start && review.reviewDate <= selectedRange.end)
  }, [reviews, selectedRange])

  const filteredDetailReviews = useMemo(() => {
    const query = reviewSearch.trim().toLowerCase()
    if (!query) return visibleReviews

    return visibleReviews.filter(review =>
      [
        review.coachName,
        review.directionName,
        review.cityName,
        review.studioAddress,
        review.reviewerDisplayName,
      ].some(value => value.toLowerCase().includes(query))
    )
  }, [reviewSearch, visibleReviews])

  const reviewsPageCount = Math.max(1, Math.ceil(filteredDetailReviews.length / reviewsPageSize))
  const safeReviewsPage = Math.min(reviewsPage, reviewsPageCount)
  const pagedDetailReviews = useMemo(() => {
    const startIndex = (safeReviewsPage - 1) * reviewsPageSize
    return filteredDetailReviews.slice(startIndex, startIndex + reviewsPageSize)
  }, [filteredDetailReviews, reviewsPageSize, safeReviewsPage])

  const dashboardMetrics = useMemo(() => {
    const reviewCount = visibleReviews.length
    const totalScore = visibleReviews.reduce((sum, review) => sum + review.totalScore, 0)
    const maxScore = visibleReviews.reduce((sum, review) => sum + review.maxScore, 0)
    const averageQuality = maxScore > 0 ? (totalScore / maxScore) * 100 : 0
    const goodCount = visibleReviews.filter(review => review.qualityPercent > 80).length
    const warningCount = visibleReviews.filter(review => review.qualityPercent >= 50 && review.qualityPercent <= 80).length
    const criticalCount = visibleReviews.filter(review => review.qualityPercent < 50).length
    const directionMap = new Map<string, { label: string; count: number }>()
    const cityMap = new Map<string, { label: string; count: number }>()
    const coachMap = new Map<string, { label: string; count: number }>()
    const partnerMap = new Map<string, { label: string; count: number }>()
    for (const review of visibleReviews) {
      const key = review.directionId || review.directionName || 'unknown'
      const label = review.directionName || 'Без направления'
      const current = directionMap.get(key)
      directionMap.set(key, { label, count: (current?.count ?? 0) + 1 })

      const cityKey = review.cityName || 'unknown-city'
      const cityLabel = review.cityName || 'Без города'
      const cityCurrent = cityMap.get(cityKey)
      cityMap.set(cityKey, { label: cityLabel, count: (cityCurrent?.count ?? 0) + 1 })

      const coachKey = review.coachName || 'unknown-coach'
      const coachLabel = review.coachName || 'Без педагога'
      const coachCurrent = coachMap.get(coachKey)
      coachMap.set(coachKey, { label: coachLabel, count: (coachCurrent?.count ?? 0) + 1 })
    }

    for (const row of rows) {
      if (row.factReviewsCount <= 0) continue
      const partnerKey = row.partnerNumber == null ? 'unknown-partner' : String(row.partnerNumber)
      const partnerLabel = row.partnerNumber == null
        ? 'Франчайзи не определён'
        : formatPartnerDisplay(row.partnerNumber, row.partnerPersonsFio, row.partnerCitiesText)
      const partnerCurrent = partnerMap.get(partnerKey)
      partnerMap.set(partnerKey, { label: partnerLabel, count: (partnerCurrent?.count ?? 0) + row.factReviewsCount })
    }

    const getTop = (map: Map<string, { label: string; count: number }>): DistributionItem[] => Array.from(map.values())
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, 'ru'))
      .map((item) => {
        const pct = reviewCount > 0 ? (item.count / reviewCount) * 100 : 0
        return {
          label: item.label,
          primaryText: `${pct.toFixed(0)}%`,
          secondaryText: String(item.count),
          barValue: pct,
          sortBucket: 0,
          sortValue: pct,
        }
      })

    const topPlanCompletion: PlanCompletionDistributionItem[] = planMatrixRows
      .map((row) => {
        const slice = getPlanCompletionSlice(row, selectedRange?.weekNumber ?? null)
        return { row, ...slice }
      })
      .map((item) => {
        const { row, plan, fact } = item
        const completionPct = plan > 0 ? (fact / plan) * 100 : null
        const label = `Студия №${row.studioNumber} · ${row.cityName}`
        if (fact <= 0 && plan <= 0) {
          return {
            label,
            primaryText: 'Нет данных',
            secondaryText: 'План не задан',
            barValue: 0,
            sortBucket: 3,
            sortValue: 0,
            hasFact: false,
          }
        }

        if (fact <= 0) {
          return {
            label,
            primaryText: '0%',
            secondaryText: `0 / ${formatNumber(plan)}`,
            barValue: 0,
            sortBucket: 2,
            sortValue: plan,
            hasFact: false,
          }
        }

        if (completionPct == null) {
          return {
            label,
            primaryText: 'Нет плана',
            secondaryText: `${formatNumber(fact)} провер${fact === 1 ? 'ка' : fact < 5 ? 'ки' : 'ок'}`,
            barValue: 0,
            sortBucket: 1,
            sortValue: fact,
            hasFact: true,
          }
        }

        return {
          label,
          primaryText: `${completionPct.toFixed(0)}%`,
          secondaryText: `${formatNumber(fact)} / ${formatNumber(plan)}`,
          barValue: Math.min(completionPct, 100),
          sortBucket: 0,
          sortValue: completionPct,
          hasFact: true,
        }
      })
      .sort((left, right) => {
        if (left.sortBucket !== right.sortBucket) return left.sortBucket - right.sortBucket
        if (right.sortValue !== left.sortValue) return right.sortValue - left.sortValue
        return left.label.localeCompare(right.label, 'ru')
      })

    return {
      averageQuality,
      goodCount,
      warningCount,
      criticalCount,
      topDirections: getTop(directionMap),
      topCities: getTop(cityMap),
      topCoaches: getTop(coachMap),
      topPartners: getTop(partnerMap),
      topPlanCompletion,
      reviewCount,
    }
  }, [planMatrixRows, rows, selectedRange?.weekNumber, visibleReviews])

  const collapsedPlanCompletionItems = useMemo(
    () => dashboardMetrics.topPlanCompletion.filter((item) => item.hasFact),
    [dashboardMetrics.topPlanCompletion]
  )

  const hiddenPlanCompletionRowsCount = dashboardMetrics.topPlanCompletion.length - collapsedPlanCompletionItems.length

  const distributionItems = useMemo(() => {
    if (distributionMode === 'city') return dashboardMetrics.topCities
    if (distributionMode === 'partner') return dashboardMetrics.topPartners
    if (distributionMode === 'coach') return dashboardMetrics.topCoaches
    if (distributionMode === 'plan_completion') {
      return showAllPlanCompletionRows ? dashboardMetrics.topPlanCompletion : collapsedPlanCompletionItems
    }
    return dashboardMetrics.topDirections
  }, [
    collapsedPlanCompletionItems,
    dashboardMetrics.topCities,
    dashboardMetrics.topCoaches,
    dashboardMetrics.topDirections,
    dashboardMetrics.topPartners,
    dashboardMetrics.topPlanCompletion,
    distributionMode,
    showAllPlanCompletionRows,
  ])

  const partnerLabels = useMemo(() => {
    const unique = new Map<string, string>()
    for (const partner of assignedPartners) {
      const label = formatPartnerDisplay(partner.partner_number, partner.persons_fio, partner.cities_text)
      unique.set(String(partner.partner_number), label)
    }
    if (unique.size === 0) {
      for (const row of rows) {
        if (row.partnerNumber == null) continue
        const label = formatPartnerDisplay(row.partnerNumber, row.partnerPersonsFio, row.partnerCitiesText)
        unique.set(String(row.partnerNumber), label)
      }
    }
    return Array.from(unique.values())
  }, [assignedPartners, rows])

  const periodPartnerCount = useMemo(
    () => new Set(assignedPartners.map((partner) => partner.partner_number)).size,
    [assignedPartners]
  )

  const periodStudioCount = useMemo(
    () => assignedPartners.reduce((sum, partner) => sum + Number(partner.studio_count ?? 0), 0),
    [assignedPartners]
  )

  const partnerCount = periodPartnerCount > 0
    ? periodPartnerCount
    : (specialistDashboardRow?.partnersCount ?? partnerLabels.length)
  const inScopeStudiosCount = periodStudioCount > 0
    ? periodStudioCount
    : (specialistDashboardRow?.studiosCount ?? summary.inScopeStudios)

  const weekNumbers = useMemo(() => {
    const weeks = timeFilterOptions
      .filter((option) => option.value.startsWith('week_'))
      .map((option) => Number.parseInt(option.value.replace('week_', ''), 10))
      .filter((value) => Number.isFinite(value) && value > 0)
    return weeks.length > 0 ? weeks : [1, 2, 3, 4]
  }, [timeFilterOptions])

  const rowsNeedingDistribution = useMemo(
    () => planMatrixRows.filter((row) => {
      const draft = draftPlan[row.studioInternalId] ?? toWeeklyPlanDraft(row)
      const availableWeekNumbers = getAvailableWeekNumbers(row, weekNumbers)
      return availableWeekNumbers.length > 0
        && hasOnlyPeriodTotalFilled(draft, distributionTotals[row.studioInternalId], availableWeekNumbers)
    }),
    [distributionTotals, draftPlan, planMatrixRows, weekNumbers]
  )

  const hasRowsNeedingDistribution = rowsNeedingDistribution.length > 0

  const planEditingWindow = useMemo(() => {
    if (!selectedPeriod?.period_start) return null

    const periodStart = new Date(`${selectedPeriod.period_start}T00:00:00`)
    if (Number.isNaN(periodStart.getTime())) return null

    const start = new Date(periodStart)
    start.setDate(start.getDate() - 3)

    const end = new Date(periodStart)
    end.setDate(end.getDate() + 4)

    return { start, end }
  }, [selectedPeriod?.period_start])

  const canEditPlan = useMemo(() => {
    if (canManagePedagogy) return true
    if (!isPedagogySpecialist) return false
    if (!planEditingWindow) return false

    const today = new Date()
    const currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())

    return currentDate >= planEditingWindow.start && currentDate <= planEditingWindow.end
  }, [canManagePedagogy, isPedagogySpecialist, planEditingWindow])

  const planEditRestrictionMessage = useMemo(() => {
    if (canEditPlan || !planEditingWindow) return ''
    return `Для специалиста редактирование плана открыто с ${formatShortDate(formatIsoDate(planEditingWindow.start))} по ${formatShortDate(formatIsoDate(planEditingWindow.end))}.`
  }, [canEditPlan, planEditingWindow])

  const isDirty = useMemo(() => {
    return planMatrixRows.some((row) => {
      const draft = draftPlan[row.studioInternalId] ?? toWeeklyPlanDraft(row)
      const availableWeekNumbers = getAvailableWeekNumbers(row, weekNumbers)
      return availableWeekNumbers.some((weekNumber) => {
        const key = `week${weekNumber}` as keyof WeeklyPlanDraft
        const draftValue = normalizeWeekValue(draft[key], false)
        const currentValue = Number(row[`planWeek${weekNumber}` as keyof PedagogyWeeklyPlanMatrixRow] ?? 0)
        return draftValue !== currentValue
      })
    })
  }, [draftPlan, planMatrixRows, weekNumbers])

  const planFilterCounts = useMemo(() => {
    return planMatrixRows.reduce(
      (acc, row) => {
        const status = getSavedPlanCompletenessStatus(row, weekNumbers)
        if (status !== 'not_applicable') acc[status] += 1
        acc.all += 1
        return acc
      },
      { all: 0, no_plan: 0, partial_plan: 0, full_plan: 0 } as Record<PlanCompletenessFilter, number>
    )
  }, [planMatrixRows, weekNumbers])

  const filteredPlanMatrixRows = useMemo(() => {
    return planMatrixRows.filter((row) => {
      if (planCompletenessFilter === 'all') return true
      return getSavedPlanCompletenessStatus(row, weekNumbers) === planCompletenessFilter
    })
  }, [planCompletenessFilter, planMatrixRows, weekNumbers])

  const switchTab = (tab: SpecialistTab) => {
    if (tab === 'teachers') {
      navigate(`${pedagogyPaths.teachers}?specialist=${encodeURIComponent(specialistUserId)}`)
      return
    }
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next)
  }

  const reloadPlanMatrix = async () => {
    if (!selectedPeriodId) return
    const [detailRows] = await Promise.all([
      getPedagogySpecialistPeriodDetails(specialistUserId, selectedPeriodId, selectedRange?.weekNumber ?? null),
    ])
    setRows(detailRows)
    setPlanMatrixRows([])
    setDraftPlan({})
  }

  const reloadReviewMetrics = async () => {
    if (selectedPeriodId == null) return

    const [detailRows, specialistReviews, dashboardRows] = await Promise.all([
      getPedagogySpecialistPeriodDetails(specialistUserId, selectedPeriodId, selectedRange?.weekNumber ?? null),
      listAllTrainerReviewSummary({ reviewerUserId: specialistUserId }),
      listPedagogyDashboard(selectedPeriodId, selectedRange?.weekNumber ?? null),
    ])

    setRows(detailRows)
    setReviews(specialistReviews)
    setPlanMatrixRows([])
    setSpecialistDashboardRow(
      dashboardRows.find(row => row.specialistUserId === specialistUserId) ?? null
    )
    setDraftPlan({})
    setDistributionTotals({})
  }

  const handleArchiveReview = async (review: TrainerReviewSummaryRow) => {
    if (!canManagePedagogy || archivingReviewId) return

    const coachName = review.coachName.trim() || 'Без педагога'
    const reviewDate = formatTrainingDate(review.trainingAt)
    const confirmed = window.confirm(
      `Удалить отсмотр педагога «${coachName}» от ${reviewDate}?\n\n` +
      'Запись будет перемещена в архив и перестанет учитываться в показателях.'
    )
    if (!confirmed) return

    setArchivingReviewId(review.reviewId)
    setError('')
    setSaveMessage('')

    try {
      await archiveTrainerReview(review.reviewId)
      setReviews(current => current.filter(item => item.reviewId !== review.reviewId))

      try {
        await reloadReviewMetrics()
        setSaveMessage('Отсмотр перемещён в архив и исключён из показателей.')
      } catch (reloadError) {
        console.error('Trainer review archived, but metrics reload failed:', reloadError)
        setError('Отсмотр перемещён в архив, но показатели не удалось обновить. Обновите страницу.')
      }
    } catch (archiveError) {
      console.error('Failed to archive trainer review:', archiveError)
      setError(archiveError instanceof Error ? archiveError.message : 'Не удалось удалить отсмотр')
    } finally {
      setArchivingReviewId(null)
    }
  }

  const handleSavePlanRows = async (targetStudioIds?: number[]) => {
    if (!selectedPeriodId) return
    if (!canEditPlan) {
      setError(planEditRestrictionMessage || 'Сейчас редактирование плана недоступно')
      return
    }

    const targetRows = planMatrixRows.filter((row) => targetStudioIds == null || targetStudioIds.includes(row.studioInternalId))
    const blockedRows = targetRows.filter((row) => {
      const draft = draftPlan[row.studioInternalId] ?? toWeeklyPlanDraft(row)
      const availableWeekNumbers = getAvailableWeekNumbers(row, weekNumbers)
      return availableWeekNumbers.length > 0
        && hasOnlyPeriodTotalFilled(draft, distributionTotals[row.studioInternalId], availableWeekNumbers)
    })
    if (blockedRows.length > 0) {
      setError(
        blockedRows.length === 1
          ? 'Сначала распределите итог по неделям в этой строке.'
          : 'Сначала распределите итог по неделям в строках, где заполнен только итог за период.'
      )
      return
    }

    const partialRows = targetRows.filter((row) => {
      const draft = draftPlan[row.studioInternalId] ?? toWeeklyPlanDraft(row)
      return getPlanCompletenessStatus(draft, getAvailableWeekNumbers(row, weekNumbers)) === 'partial_plan'
    })

    if (partialRows.length > 0) {
      const confirmed = confirm(
        partialRows.length === 1
          ? 'Часть недель останется без плана. Сохранить как есть?'
          : 'В некоторых строках часть недель останется без плана. Сохранить как есть?'
      )
      if (!confirmed) return
    }

    setSaving(true)
    setSaveMessage('')
    setError('')

    try {
      const payload = targetRows.map((row) => {
        const draft = draftPlan[row.studioInternalId] ?? toWeeklyPlanDraft(row)
        return {
          studioInternalId: row.studioInternalId,
          week1: row.canPlanWeek1 ? normalizeWeekValue(draft.week1) : 0,
          week2: row.canPlanWeek2 ? normalizeWeekValue(draft.week2) : 0,
          week3: row.canPlanWeek3 ? normalizeWeekValue(draft.week3) : 0,
          week4: row.canPlanWeek4 ? normalizeWeekValue(draft.week4) : 0,
          week5: row.canPlanWeek5 ? normalizeWeekValue(draft.week5) : 0,
        }
      })

      await savePedagogyWeeklyPlan({
        specialistUserId,
        periodId: selectedPeriodId,
        rows: payload,
      })

      await reloadPlanMatrix()
      setSaveMessage(targetStudioIds == null ? 'План сохранён' : 'Строка сохранена')
    } catch (saveError) {
      console.error('Failed to save pedagogy plan:', saveError)
      setError(saveError instanceof Error ? saveError.message : 'Не удалось сохранить план')
    } finally {
      setSaving(false)
    }
  }

  const handleClearPlanRows = (targetStudioIds?: number[]) => {
    if (!canEditPlan) {
      setError(planEditRestrictionMessage || 'Сейчас редактирование плана недоступно')
      return
    }

    setDraftPlan((prev) => {
      const next = { ...prev }
      const targetRows = planMatrixRows.filter((row) => targetStudioIds == null || targetStudioIds.includes(row.studioInternalId))
      for (const row of targetRows) {
        next[row.studioInternalId] = {
          week1: '',
          week2: '',
          week3: '',
          week4: '',
          week5: '',
        }
      }
      return next
    })
    setDistributionTotals((prev) => {
      const next = { ...prev }
      const targetRows = planMatrixRows.filter((row) => targetStudioIds == null || targetStudioIds.includes(row.studioInternalId))
      for (const row of targetRows) {
        delete next[row.studioInternalId]
      }
      return next
    })
  }

  const applyDraftPatch = (studioInternalId: number, patch: Partial<WeeklyPlanDraft>) => {
    setDraftPlan((prev) => ({
      ...prev,
      [studioInternalId]: {
        ...(prev[studioInternalId] ?? { week1: '', week2: '', week3: '', week4: '', week5: '' }),
        ...patch,
      },
    }))
    setDistributionTotals((prev) => {
      if (!(studioInternalId in prev)) return prev
      const next = { ...prev }
      delete next[studioInternalId]
      return next
    })
  }

  const fillWeekToAll = (studioInternalId: number, weekNumber: number) => {
    if (!canEditPlan) return

    const fallbackDraft = planMatrixRows.find((row) => row.studioInternalId === studioInternalId)
    const sourceDraft = draftPlan[studioInternalId]
      ?? (fallbackDraft ? toWeeklyPlanDraft(fallbackDraft) : { week1: '', week2: '', week3: '', week4: '', week5: '' })
    const key = `week${weekNumber}` as keyof WeeklyPlanDraft
    const value = sourceDraft[key]
    const row = planMatrixRows.find((item) => item.studioInternalId === studioInternalId)
    const availableWeekNumbers = row ? getAvailableWeekNumbers(row, weekNumbers) : []
    const patch: Partial<WeeklyPlanDraft> = {}
    for (const item of availableWeekNumbers) {
      patch[`week${item}` as keyof WeeklyPlanDraft] = value
    }
    applyDraftPatch(studioInternalId, patch)
  }

  const distributeTotalAcrossWeeks = (studioInternalId: number, totalValue: string) => {
    if (!canEditPlan) return

    const total = normalizeWeekValue(totalValue)
    const row = planMatrixRows.find((item) => item.studioInternalId === studioInternalId)
    const availableWeekNumbers = row ? getAvailableWeekNumbers(row, weekNumbers) : []
    if (availableWeekNumbers.length === 0) return
    const weeksCount = availableWeekNumbers.length
    const base = Math.floor(total / weeksCount)
    let remainder = total % weeksCount
    const patch: Partial<WeeklyPlanDraft> = {}
    for (const weekNumber of availableWeekNumbers) {
      const nextValue = base + (remainder > 0 ? 1 : 0)
      if (remainder > 0) remainder -= 1
      patch[`week${weekNumber}` as keyof WeeklyPlanDraft] = nextValue > 0 ? String(nextValue) : ''
    }
    applyDraftPatch(studioInternalId, patch)
    setDistributionTotals((prev) => ({ ...prev, [studioInternalId]: '' }))
  }

  // Проверка доступа не дошла до сервера: страница всё равно не сможет
  // загрузить ни одной RPC, поэтому объясняем причину, а не показываем
  // каркас, который молча останется пустым.
  if (accessError) return <PedagogyAccessErrorCard onRetry={retryAccessCheck} />

  return (
    <div className="space-y-4 pb-24">
      <PedagogyBackLink to={pedagogyPaths.department} label="К своду" />

      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <UserRound size={16} className="text-primary" />
            Специалист по педагогам
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{specialistName}</h1>
          <div className="mt-2 flex flex-wrap gap-2 text-sm text-muted-foreground">
            <span>Франчайзи: {partnerCount}</span>
            <span>Студии в контуре: {inScopeStudiosCount}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => navigate(pedagogyPaths.reviewNew)}
            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap"
          >
            <ClipboardList size={16} className="mr-1" />
            Новый отсмотр
          </Button>
        </div>
      </div>

      <PeriodSelector
        currentPeriod={selectedPeriod}
        onPrevPeriod={() => {
          if (selectedPeriodIndex < 0 || selectedPeriodIndex >= periods.length - 1) return
          setSelectedPeriodId(periods[selectedPeriodIndex + 1]?.period_id ?? null)
        }}
        onNextPeriod={() => {
          if (selectedPeriodIndex <= 0) return
          setSelectedPeriodId(periods[selectedPeriodIndex - 1]?.period_id ?? null)
        }}
        canGoPrev={selectedPeriodIndex >= 0 && selectedPeriodIndex < periods.length - 1}
        canGoNext={selectedPeriodIndex > 0}
        showViewModeSelector={true}
        isCurrentPeriod={true}
        periodFilterValue={selectedTimeFilter}
        periodFilterOptions={timeFilterOptions}
        onPeriodFilterChange={setSelectedTimeFilter}
        selectedRange={selectedRange}
      />

      <div className="grid gap-3 xl:grid-cols-3">
        <Card className="border border-border shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">Среднее качество</div>
            <div className="text-xs text-muted-foreground">Отсмотров: {formatNumber(dashboardMetrics.reviewCount)}</div>
          </div>
          <div className="mt-2 text-3xl font-bold tabular-nums">
            {dashboardMetrics.reviewCount === 0 ? '—' : `${dashboardMetrics.averageQuality.toFixed(1)}%`}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {selectedRange?.label ?? 'По выбранному периоду'}
          </div>
        </Card>
        <Card className="border border-border shadow-sm">
          <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>Факт / план</span>
            <span className="font-semibold tabular-nums">
              {summary.completionPct == null ? '—' : `${summary.completionPct.toFixed(1)}%`}
            </span>
          </div>
          <div className="mt-2 text-3xl font-bold tabular-nums">
            {formatNumber(summary.fact)} / {formatNumber(summary.plan)}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">Выполнение за выбранный срез</div>
          <div className="mt-4">
            <ProgressBar value={summary.completionPct ?? 0} fillClassName="bg-primary" />
          </div>
        </Card>
        <Card className="border border-border shadow-sm">
          <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>Осталось</span>
            <span className="text-xs">
              Дней: {planPace.daysLeft == null ? '—' : formatNumber(planPace.daysLeft)}
            </span>
          </div>
          <div className="mt-2 text-3xl font-bold tabular-nums">{formatNumber(summary.remaining)}</div>
          <div className="mt-2 text-sm text-muted-foreground">
            Нужно в день: {planPace.perDay == null ? '—' : formatNumber(planPace.perDay)}
          </div>
        </Card>
      </div>

      <Card className="border border-border shadow-sm">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPartnersSheetOpen(true)}
            className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-foreground transition hover:bg-accent"
          >
            Франчайзи: {formatNumber(partnerCount)}
          </button>
          <span className="inline-flex items-center rounded-full border border-border bg-muted px-3 py-1 text-xs text-muted-foreground">
            Критичных точек внимания нет
          </span>
        </div>
      </Card>

      <div className="overflow-x-auto border-b border-border scrollbar-hide">
        <div className="flex min-w-max gap-5">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => switchTab(tab.id)}
              className={`relative pb-2.5 pt-1 text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && <span className="absolute inset-x-0 bottom-[-1px] h-0.5 rounded-full bg-primary" />}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <Card className="border border-error/30 bg-error/5 shadow-sm">
          <div className="text-sm text-error">{error}</div>
        </Card>
      )}

      {!canEditPlan && activeTab === 'plan' && planEditRestrictionMessage && (
        <Card className="border border-warning/30 bg-warning/5 shadow-sm">
          <div className="text-sm text-warning-foreground">{planEditRestrictionMessage}</div>
        </Card>
      )}

      {saveMessage && (
        <Card className="border border-success/30 bg-success/5 shadow-sm">
          <div className="text-sm text-success">{saveMessage}</div>
        </Card>
      )}

      {loading ? (
        <Card><div className="text-sm text-muted-foreground">Загрузка данных специалиста...</div></Card>
      ) : activeTab === 'overview' ? (
        <div className="space-y-4">
          <div className="grid gap-4 xl:grid-cols-[0.88fr_1.12fr]">
            <Card className="border border-border shadow-sm">
              <div className="flex items-center justify-between gap-2 text-sm font-semibold">
                <div className="flex items-center gap-2">
                  <Target size={16} className="text-primary" />
                  Качество тренировок
                </div>
                <span className="text-xs text-muted-foreground">Распределение по качеству</span>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <QualityMiniCard
                  label="Хорошее качество"
                  value={dashboardMetrics.goodCount}
                  pct={dashboardMetrics.reviewCount > 0 ? (dashboardMetrics.goodCount / dashboardMetrics.reviewCount) * 100 : 0}
                  tone="good"
                />
                <QualityMiniCard
                  label="Зона внимания"
                  value={dashboardMetrics.warningCount}
                  pct={dashboardMetrics.reviewCount > 0 ? (dashboardMetrics.warningCount / dashboardMetrics.reviewCount) * 100 : 0}
                  tone="warning"
                />
                <QualityMiniCard
                  label="Критично"
                  value={dashboardMetrics.criticalCount}
                  pct={dashboardMetrics.reviewCount > 0 ? (dashboardMetrics.criticalCount / dashboardMetrics.reviewCount) * 100 : 0}
                  tone="critical"
                />
              </div>
            </Card>

            <Card className="border border-border shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <CalendarDays size={16} className="text-primary" />
                  Распределение отсмотров
                </div>
                <select
                  value={distributionMode}
                  onChange={event => {
                    const nextMode = event.target.value as DistributionMode
                    setDistributionMode(nextMode)
                    if (nextMode !== 'plan_completion') {
                      setShowAllPlanCompletionRows(false)
                    }
                  }}
                  className="input h-9 w-[180px] px-3 py-1 text-sm"
                >
                  <option value="direction">По направлениям</option>
                  <option value="city">По городам</option>
                  <option value="partner">По франчайзи</option>
                  <option value="coach">По педагогам</option>
                </select>
              </div>
              <div className="mt-4 space-y-3">
                {distributionItems.length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    {distributionMode === 'plan_completion'
                      ? 'Нет студий, где есть план или проведённые проверки.'
                      : 'В выбранном периоде отсмотров пока нет.'}
                  </div>
                )}
                {distributionItems.map(item => {
                  return (
                    <div key={item.label} className="grid grid-cols-[minmax(0,1fr)_48px] items-center gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <span className="truncate">{item.label}</span>
                          <span className="tabular-nums">{item.primaryText}</span>
                        </div>
                        <div className="mt-2">
                          <ProgressBar value={item.barValue} fillClassName="bg-primary" />
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground tabular-nums">
                        {item.secondaryText}
                      </div>
                    </div>
                  )
                })}
                {distributionMode === 'plan_completion' && hiddenPlanCompletionRowsCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllPlanCompletionRows((prev) => !prev)}
                    className="pt-1 text-sm font-medium text-primary transition-colors hover:text-primary/80"
                  >
                    {showAllPlanCompletionRows
                      ? 'Свернуть список'
                      : `Показать остальные студии (${hiddenPlanCompletionRowsCount})`}
                  </button>
                )}
              </div>
            </Card>
          </div>
        </div>
      ) : activeTab === 'plan' ? (
        <Card className="border border-border shadow-sm">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <div className="text-sm font-semibold">Недельная матрица планирования</div>
              <div className="text-sm text-muted-foreground">
                План доступен только в тех неделях, когда специалист отвечал за франчайзи хотя бы один день. Недоступные недели отмечены «—». «На все» и «Распределить» работают только по доступным неделям.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => handleClearPlanRows()} disabled={saving || !canEditPlan}>
                <Eraser size={16} className="mr-1" />
                Очистить всё
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {([
              ['all', `Все студии · ${formatNumber(planFilterCounts.all)}`],
              ['no_plan', `Нет плана · ${formatNumber(planFilterCounts.no_plan)}`],
              ['partial_plan', `План неполный · ${formatNumber(planFilterCounts.partial_plan)}`],
              ['full_plan', `План есть · ${formatNumber(planFilterCounts.full_plan)}`],
            ] as Array<[PlanCompletenessFilter, string]>).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setPlanCompletenessFilter(value)}
                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                  planCompletenessFilter === value
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:bg-accent'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[1320px] w-full table-fixed text-sm">
              <thead className="text-left text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="w-[260px] px-3 py-3 font-medium">Студия</th>
                  <th className="w-[300px] px-3 py-3 font-medium">Франчайзи</th>
                  {weekNumbers.map((weekNumber) => (
                    <th
                      key={weekNumber}
                      className={`px-3 py-3 text-center font-medium ${selectedRange?.weekNumber === weekNumber ? 'bg-primary/5 text-foreground' : ''}`}
                    >
                      Неделя {weekNumber}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center font-medium">Итого</th>
                  <th className="px-3 py-3 font-medium">Статус</th>
                  <th className="px-3 py-3 font-medium">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlanMatrixRows.length === 0 && (
                  <tr>
                    <td colSpan={weekNumbers.length + 4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                      {planMatrixRows.length === 0
                        ? 'На выбранный период в контуре специалиста пока нет студий для планирования. План можно задать только по тем студиям, которые назначены специалисту на этот период.'
                        : 'Под выбранный фильтр сейчас нет студий.'}
                    </td>
                  </tr>
                )}
                {filteredPlanMatrixRows.map(row => {
                  const draft = draftPlan[row.studioInternalId] ?? toWeeklyPlanDraft(row)
                  const availableWeekNumbers = getAvailableWeekNumbers(row, weekNumbers)
                  const planStatus = getPlanCompletenessStatus(draft, availableWeekNumbers)
                  const planStatusBadge = getPlanCompletenessBadge(planStatus)
                  const totalValue = sumWeeklyDraft(draft, availableWeekNumbers)
                  const needsDistribution = availableWeekNumbers.length > 0
                    && hasOnlyPeriodTotalFilled(draft, distributionTotals[row.studioInternalId], availableWeekNumbers)
                  return (
                    <tr key={row.studioInternalId} className="border-b border-border/70 align-top">
                      <td className="w-[260px] px-3 py-3">
                        <div className="font-medium">Студия №{row.studioNumber}</div>
                        <div className="text-xs text-muted-foreground">
                          {row.cityName} · {row.address || 'Адрес не указан'}
                        </div>
                      </td>
                      <td className="w-[300px] px-3 py-3">
                        <div className="break-words">
                          {row.partnerNumber != null
                            ? formatPartnerDisplay(row.partnerNumber, row.partnerPersonsFio, row.partnerCitiesText)
                            : 'Франчайзи не определён'}
                        </div>
                      </td>
                      {weekNumbers.map((weekNumber) => {
                        const key = `week${weekNumber}` as keyof WeeklyPlanDraft
                        const factKey = `factWeek${weekNumber}` as keyof PedagogyWeeklyPlanMatrixRow
                        const factValue = Number(row[factKey] ?? 0)
                        const weekAvailable = canPlanWeek(row, weekNumber)
                        return (
                          <td
                            key={weekNumber}
                            className={`px-3 py-3 ${selectedRange?.weekNumber === weekNumber ? 'bg-primary/5' : ''}`}
                          >
                            {weekAvailable ? (
                              <div className="space-y-2">
                                <input
                                  value={draft[key]}
                                  onChange={event => applyDraftPatch(row.studioInternalId, { [key]: event.target.value } as Partial<WeeklyPlanDraft>)}
                                  inputMode="numeric"
                                  className="input h-9 w-24 px-3 py-1 text-center tabular-nums"
                                  placeholder="0"
                                  disabled={!canEditPlan}
                                />
                                <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                                  <span>Факт: {formatNumber(factValue)}</span>
                                  {(draft[key] || '').trim() !== '' && (
                                    <button
                                      type="button"
                                      onClick={() => fillWeekToAll(row.studioInternalId, weekNumber)}
                                      className="font-medium text-primary hover:underline"
                                      disabled={!canEditPlan}
                                    >
                                      На все
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex min-h-9 flex-col items-center justify-center text-muted-foreground">
                                <span className="text-lg leading-none">—</span>
                                {factValue > 0 && <span className="mt-1 text-[11px]">Факт: {formatNumber(factValue)}</span>}
                              </div>
                            )}
                          </td>
                        )
                      })}
                      <td className="px-3 py-3">
                        <div className="rounded-xl border border-border bg-muted/40 p-3">
                          <input
                            value={distributionTotals[row.studioInternalId] ?? ''}
                            onChange={(event) => setDistributionTotals((prev) => ({ ...prev, [row.studioInternalId]: event.target.value }))}
                            inputMode="numeric"
                            className="input h-9 w-24 px-3 py-1 text-center tabular-nums"
                            placeholder={String(totalValue)}
                            disabled={!canEditPlan || availableWeekNumbers.length === 0}
                          />
                          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                            <span className="whitespace-nowrap text-muted-foreground">Факт: {formatNumber(row.factTotal)}</span>
                            {((distributionTotals[row.studioInternalId] ?? '').trim() !== '' || totalValue > 0) && (
                              <button
                                type="button"
                                onClick={() => distributeTotalAcrossWeeks(
                                  row.studioInternalId,
                                  (distributionTotals[row.studioInternalId] ?? '').trim() || String(totalValue)
                                )}
                                className="whitespace-nowrap font-medium text-primary hover:underline"
                                disabled={!canEditPlan || availableWeekNumbers.length === 0}
                              >
                                Распределить
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <Badge text={planStatusBadge.text} variant={planStatusBadge.variant} className="text-xs" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col items-start gap-2 whitespace-nowrap">
                          <Button size="sm" variant="outline" onClick={() => handleClearPlanRows([row.studioInternalId])} disabled={saving || !canEditPlan || availableWeekNumbers.length === 0}>
                            Очистить
                          </Button>
                          <Button size="sm" onClick={() => void handleSavePlanRows([row.studioInternalId])} disabled={saving || !canEditPlan || availableWeekNumbers.length === 0 || needsDistribution}>
                            Сохранить
                          </Button>
                          {needsDistribution && (
                            <div className="max-w-[140px] text-[11px] text-muted-foreground">
                              Сначала распределите итог по неделям.
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end">
            <div className="flex flex-col items-end">
              <Button size="sm" onClick={() => void handleSavePlanRows()} disabled={!isDirty || saving || !canEditPlan || hasRowsNeedingDistribution}>
                <Save size={16} className="mr-1" />
                {saving ? 'Сохраняем...' : 'Сохранить всё'}
              </Button>
              {hasRowsNeedingDistribution && (
                <div className="mt-2 max-w-[320px] text-right text-xs text-muted-foreground">
                  Сначала распределите итог по неделям в строках, где заполнен только итог за период.
                </div>
              )}
            </div>
          </div>
        </Card>
      ) : activeTab === 'reviews' ? (
        <Card className="border border-border shadow-sm">
          <div>
            <div className="text-sm font-semibold">Отсмотры специалиста</div>
            <div className="text-sm text-muted-foreground">Показываем только отсмотры выбранного периода этого специалиста.</div>
          </div>
          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <label className="block max-w-md flex-1 space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">Поиск</span>
              <input
                value={reviewSearch}
                onChange={event => setReviewSearch(event.target.value)}
                className="input w-full"
                placeholder="Педагог, направление, город, адрес"
              />
            </label>
            <label className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">На странице</span>
              <select
                value={reviewsPageSize}
                onChange={event => setReviewsPageSize(Number(event.target.value))}
                className="input w-28"
              >
                {PAGE_SIZE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-4">
            {filteredDetailReviews.length === 0 && (
              <div className="text-sm text-muted-foreground">За этот период отсмотров пока нет.</div>
            )}
            {filteredDetailReviews.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1160px] table-fixed text-sm">
                  <thead className="text-left text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="w-[220px] px-3 py-3 font-medium">Студия</th>
                      <th className="w-[190px] px-3 py-3 font-medium">Педагог</th>
                      <th className="w-[200px] px-3 py-3 font-medium">Направление</th>
                      <th className="w-[220px] px-3 py-3 font-medium">Дата и время тренировки</th>
                      <th className="w-[80px] px-3 py-3 text-right font-medium">%</th>
                      <th className="w-[250px] px-3 py-3 text-right font-medium">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedDetailReviews.map(review => {
                      const studioMeta = review.studioInternalId == null
                        ? null
                        : (studios.find(studio => studio.studio_internal_id === review.studioInternalId) ?? null)
                      const studioNumber = studioMeta?.studio_number?.trim() || (review.studioInternalId == null ? '' : String(review.studioInternalId))

                      return (
                      <tr key={review.reviewId} className="border-b border-border/70 align-top">
                        <td className="w-[220px] px-3 py-3">
                          <div className="font-medium">{studioNumber ? `Студия ${studioNumber}` : 'Студия не указана'}</div>
                          <div className="text-xs text-muted-foreground">{review.cityName || 'Без города'}</div>
                          <div className="text-xs text-muted-foreground">{review.studioAddress || 'Адрес не указан'}</div>
                        </td>
                        <td className="w-[190px] px-3 py-3 font-medium">{review.coachName || 'Без педагога'}</td>
                        <td className="w-[200px] px-3 py-3">{review.directionName || 'Без направления'}</td>
                        <td className="w-[220px] px-3 py-3 tabular-nums">
                          <div>{formatTrainingDate(review.trainingAt)}</div>
                          <div className="text-xs text-muted-foreground">{formatTrainingTime(review.trainingAt)}</div>
                        </td>
                        <td className={`w-[80px] px-3 py-3 text-right font-semibold tabular-nums ${review.qualityPercent > 80 ? 'text-success' : review.qualityPercent >= 50 ? 'text-amber-700' : 'text-error'}`}>
                          {review.qualityPercent.toFixed(1)}%
                        </td>
                        <td className="w-[250px] px-3 py-3">
                          <div className="flex flex-nowrap items-center justify-end gap-2 whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="shrink-0 whitespace-nowrap"
                              onClick={() => navigate(`${createTrainerChecklistReviewLink(review.reviewId)}&returnTo=${encodeURIComponent(returnTo)}`)}
                            >
                              Открыть
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="shrink-0 whitespace-nowrap"
                              onClick={() => openTrainerChecklistReport(review.reviewId, returnTo)}
                            >
                              Отчёт
                            </Button>
                            {canManagePedagogy && (
                              <button
                                type="button"
                                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-red-200 bg-background text-red-600 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => void handleArchiveReview(review)}
                                disabled={archivingReviewId === review.reviewId}
                                aria-label="Удалить отсмотр"
                                title="Удалить отсмотр"
                              >
                                <Trash2
                                  size={16}
                                  aria-hidden="true"
                                  className={archivingReviewId === review.reviewId ? 'animate-pulse' : undefined}
                                />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            )}
            {filteredDetailReviews.length > 0 && (
              <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  Показано {formatNumber((safeReviewsPage - 1) * reviewsPageSize + 1)}-
                  {formatNumber(Math.min(safeReviewsPage * reviewsPageSize, filteredDetailReviews.length))} из {formatNumber(filteredDetailReviews.length)}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setReviewsPage(page => Math.max(1, page - 1))} disabled={safeReviewsPage <= 1}>
                    Назад
                  </Button>
                  <div className="min-w-[88px] text-center text-sm text-muted-foreground">
                    {safeReviewsPage} / {reviewsPageCount}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setReviewsPage(page => Math.min(reviewsPageCount, page + 1))} disabled={safeReviewsPage >= reviewsPageCount}>
                    Далее
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card className="border border-border shadow-sm">
          <div className="text-sm text-muted-foreground">Неизвестная вкладка</div>
        </Card>
      )}

      <BottomSheet
        isOpen={partnersSheetOpen}
        onClose={() => setPartnersSheetOpen(false)}
        title="Франчайзи специалиста"
      >
        <div className="space-y-3 p-4">
          <div className="text-sm text-muted-foreground">
            В контуре: {formatNumber(inScopeStudiosCount)} студий, {formatNumber(partnerCount)} франчайзи.
          </div>
          {partnerLabels.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {partnerCount > 0
                ? 'Франчайзи назначены, но на выбранный период по ним не найдено студий в контуре.'
                : 'Франчайзи не назначены.'}
            </div>
          ) : (
            <div className="space-y-2">
              {partnerLabels.map(label => (
                <div key={label} className="rounded-lg border border-border px-3 py-2 text-sm">
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>
      </BottomSheet>
    </div>
  )
}

function QualityMiniCard({
  label,
  value,
  pct,
  tone,
}: {
  label: string
  value: number
  pct: number
  tone: 'good' | 'warning' | 'critical'
}) {
  const toneClasses = {
    good: 'border-success/30 bg-success/5 text-success',
    warning: 'border-amber-400/40 bg-amber-50 text-amber-700',
    critical: 'border-error/30 bg-error/5 text-error',
  }[tone]

  return (
    <div className={`rounded-lg border p-3 ${toneClasses}`}>
      <div className="text-xs">{label}</div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <div className="text-3xl font-bold tabular-nums">{pct.toFixed(0)}%</div>
        <div className="text-xs opacity-80">{formatNumber(value)} отсм.</div>
      </div>
      <div className="mt-3">
        <ProgressBar value={pct} fillClassName="bg-primary" />
      </div>
    </div>
  )
}
